<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import { computed } from 'vue';
    import { Head, useForm } from '@inertiajs/vue3';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import { useI18n } from 'vue-i18n';
    import AuthLayout from '@/layouts/AuthLayout.vue';
    import InputError from '@/components/InputError.vue';

    const { t } = useI18n();

    const props = withDefaults(
        defineProps<{
            token: string;
            email: string;
            newUser?: boolean;
        }>(),
        {
            newUser: false,
        },
    );

    const form = useForm({
        token: props.token,
        email: props.email,
        password: '',
        password_confirmation: '',
    });

    const submit = () => {
        form.post(route('password.store'), {
            onFinish: () => {
                form.reset('password', 'password_confirmation');
            },
        });
    };

    const title = computed(() => {
        return props.newUser ? t('headers.confirm-user') : t('auth.password_reset_title');
    });
</script>

<template>
    <AuthLayout
        :title="title"
        :description="t('auth.password_reset_description')"
    >
        <Head :title="title" />

        <form @submit.prevent="submit">
            <div class="grid gap-4">
                <div class="grid gap-2">
                    <Label for="email">{{ t('auth.password_reset_email') }}</Label>
                    <Input
                        id="email"
                        type="email"
                        name="email"
                        autocomplete="email"
                        v-model="form.email"
                        class="mt-1 block w-full"
                        readonly
                    />
                    <InputError
                        :message="form.errors.email"
                        class="mt-2"
                    />
                </div>

                <div class="grid gap-2">
                    <Input
                        id="password"
                        type="password"
                        name="password"
                        autocomplete="new-password"
                        v-model="form.password"
                        class="placeholder:text-foreground/80"
                        autofocus
                        :placeholder="t('auth.password_reset_password')"
                    />
                    <InputError :message="form.errors.password" />
                </div>

                <div class="grid gap-2">
                    <Input
                        id="password_confirmation"
                        type="password"
                        name="password_confirmation"
                        autocomplete="new-password"
                        v-model="form.password_confirmation"
                        class="placeholder:text-foreground/80"
                        :placeholder="t('auth.password_reset_confirm_password')"
                    />
                    <InputError :message="form.errors.password_confirmation" />
                </div>
                <Button
                    width="full"
                    :disabled="form.processing"
                    :loading="form.processing"
                >
                    {{ newUser ? t('auth.create_user') : t('auth.password_reset_send') }}
                </Button>
            </div>
        </form>
    </AuthLayout>
</template>
